import Vue from 'vue';
import axios, { AxiosResponse } from 'axios';
import { Component } from 'vue-property-decorator';
import moment from 'moment';
import { EntityType } from '@/models/EntityType';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

export interface Lock {
    refreshURL: string;
    intervalNumber: NodeJS.Timeout;
    lockedBy: string;
    locked: Date;
}

@Component
export class LockHandler extends Vue {
    created(): void {
        console.log("%c Created LockHandler", "color: purple");
        window.addEventListener('focus', this.activatedEvent);
        window.addEventListener('blur', this.deactivatedEvent);
    }
    beforeDestroy(): void {
        console.log('%c Destroying LockHandler', 'color: magenta');
        window.removeEventListener('focus', this.activatedEvent);
        window.removeEventListener('blur', this.deactivatedEvent);
        this.clearAllLocks();
    }

    activatedEvent(): void {
        this.hasFocus = true;
    }

    deactivatedEvent(): void {
        this.hasFocus = false;
    }
    private locks: Lock[] = [];
    private hasFocus = true;
    private isClearing = false;

    public async clearAllLocks(): Promise<void> {
        // Prevent concurrent execution
        if (this.isClearing) {
            console.log('clearAllLocks already running, skipping duplicate call');
            return;
        }

        // Nothing to clear
        if (this.locks.length === 0) {
            console.log('No locks to clear');
            return;
        }

        this.isClearing = true;
        console.log('%c Clearing all locks', 'color: magenta; font-weight: bold');

        const unlockPromises = this.locks.map(lock => {
            clearInterval(lock.intervalNumber);
            return axios.post(lock.refreshURL, true)
                .then(() => {
                    console.log(`Lock released: ${lock.refreshURL}`);
                })
                .catch(error => {
                    console.error(`Failed to release lock at ${lock.refreshURL}:`, error);
                });
        });

        // Wait for all unlocks to complete
        await Promise.all(unlockPromises);
        this.locks = [];
        this.isClearing = false;
    }

    public addLockWithURL(url: string, refreshInterval: number): Promise<Lock> {
        return this.refreshAtURL(url)
            .then(result => {
                const intervalNumber = setInterval(
                    () => {
                        this.refreshAtURL(url)
                            //.then(result => {
                            //})
                            .catch(error => {
                                if (error.message == `We don't have focus`) {
                                    console.warn(`Window doesn't have focus, not refreshing the lock.`);
                                }
                                if (error.response && error.response.status == 418) {
                                    const lockURL = error.config.url;
                                    console.warn(`Had to release lock at ${lockURL}`);
                                    this.releaseLockAtURL(error.config.url);
                                }
                            });
                    },
                    refreshInterval);
                const lock: Lock = {
                    refreshURL: url,
                    intervalNumber: intervalNumber,
                    lockedBy: result.data.lockedBy,
                    locked: result.data.locked
                };
                this.locks.push(lock);
                return lock;
            })
            .catch(error => {
                throw error;
            });
    }

    async refreshAtURL(url: string): Promise<AxiosResponse<any>> {
        if (!this.hasFocus) {
            throw new Error(`We don't have focus`);
        }
        return axios.post(url, false)
            .then(response => {
                return response;
            })
            .catch(error => {
                throw error;
            });
    }

    releaseLockAtURL(url: string): void {
        this.locks = this.locks.filter((value: Lock) => {
            if (value.refreshURL == url) {
                clearInterval(value.intervalNumber);
            }
            return value.refreshURL != url;
        });

        // Release lock with proper error handling
        axios.post(url, true)
            .then(() => {
                console.log(`Lock released: ${url}`);
            })
            .catch((error) => {
                console.error(`Failed to release lock at ${url}:`, error);
            });
    }

    async showLockedMessage(entityType: EntityType, entityId: string, entityRecordName: string, lockData: any): Promise<void> {
        try {
            const lockedBy = lockData.lockedBy;
            const expires = lockData.expires;
            const localExpiration = moment.utc(expires).local().format("L LT");
            const entityName = EntityType[entityType];
            const requestRelease = await this.$bvModal.msgBoxConfirm(`This ${entityName} is locked by ${lockedBy} until ${localExpiration}.`,
                {
                    title: `${entityName} Locked`,
                    cancelTitle: 'Ok',
                    okTitle: 'Request Release from User',
                    noFade: true,
                    centered: true
                });

            if (!requestRelease) return;

            try {
                await Axios.post("/notification/request-lock-release", {
                    entityType,
                    entityId,
                    entityName: entityRecordName,
                    username: lockedBy
                });
                this.$notification(NotificationOptions.success(`Sent lock release request to ${lockedBy}`));
            } catch (error) {
                console.warn(error);
                this.$notification(NotificationOptions.error("Error sending lock release request"));
            }

        } catch (err) {
            console.warn(err);
        }
    }
}
